<script setup lang="ts">
import { useListInternalRolesDetailed } from '@swipe4work/api-orval'
import type { InternalRoleDetailDto } from '@swipe4work/models-orval'
import InternalRoleFormDialog from '@/features/internal-employees/dialogs/InternalRoleFormDialog.vue'

definePage({
  path: '/interne-rollen',
  name: '/settings/InternalRoles',
  // Development/Admins may manage internal roles; global super-admins also pass (hasRole short-circuits on global).
  meta: {
    title: 'Interne rollen',
    role: {
      name: 'Admin',
      department: 'Development',
    },
  },
})

const dialog = useDynamicDialog()

const { data, isPending, isError, refetch } = useListInternalRolesDetailed()
const roles = computed(() => data.value?.roles ?? [])

function openForm(role: InternalRoleDetailDto) {
  dialog.open(InternalRoleFormDialog, {
    props: {
      header: 'Rol bewerken',
      modal: true,
      style: {
        width: 'auto',
        height: 'auto',
      },
    },
    data: { role },
    onClose: () => refetch(),
  })
}
</script>

<template>
  <div class="pb-4 space-y-4">
    <Card>
      <template #content>
        <div v-if="isPending" class="flex flex-col gap-2">
          <Skeleton v-for="n in 6" :key="n" height="3rem" />
        </div>
        <Message v-else-if="isError" severity="error">
          Er is iets misgegaan bij het laden van de rollen.
        </Message>
        <div v-else class="flex flex-col gap-4 overflow-x-auto">
          <div class="flex items-center justify-between gap-4">
            <h2 class="text-lg font-semibold">
              Interne rollen
            </h2>
          </div>
          
          <DataTable :value="roles" size="small" row-hover class="w-full">
            <template #empty>
              <span class="text-sm text-surface-500">Nog geen rollen. Maak er één aan.</span>
            </template>

            <Column header="Rol" style="width: 10rem">
              <template #body="{ data: role }">
                <span class="text-sm font-medium">{{ role.name }}</span>
              </template>
            </Column>

            <Column header="Afdeling" style="width: 12rem">
              <template #body="{ data: role }">
                <span class="text-sm">{{ role.department }}</span>
              </template>
            </Column>

            <Column header="Omschrijving">
              <template #body="{ data: role }">
                <span class="text-sm text-surface-600">{{ role.description }}</span>
              </template>
            </Column>

            <Column header="Rechten" style="width: 8rem">
              <template #body="{ data: role }">
                <Tag :value="`${role.permissions.length}`" severity="secondary" />
              </template>
            </Column>

            <Column style="width: 8rem">
              <template #body="{ data: role }">
                <div class="flex justify-end gap-1">
                  <Button icon="fa-regular fa-pen" severity="secondary" text size="small" aria-label="Bewerken" @click="openForm(role)" />
                </div>
              </template>
            </Column>
          </DataTable>
        </div>
      </template>
    </Card>
  </div>
</template>
